import React, { useState } from 'react';
import HostSessionButton from '../../components/Collab/HostSessionButton';
import JoinSessionButton from '../../components/Collab/JoinSessionButton';
import SessionIdModal from '../../components/Collab/SessionIdModal';
import { LoginModal } from '../../components/Auth/LoginModal';
import { RegisterModal } from '../../components/Auth/RegisterModal';
import { UserAvatarSimple } from '../../components/Auth/UserAvatarSimple';
import './CollabPanel.css';
import { FaRegCopy } from 'react-icons/fa';
import { FaCrown } from 'react-icons/fa';
import * as signalR from '@microsoft/signalr';
import { MdLogout } from 'react-icons/md';
import { useCollab } from '../../contexts/CollabContext';
import { useAuth } from '../../contexts/AuthContext';
import { Mode } from 'types/modes';
import { CollabErrorMap } from 'types/CollabErrorMap';
import { useTranslation } from 'react-i18next';

interface SessionUser {
  name: string;
  color: string;
  isHost?: boolean;
}

interface CollabPanelProps {
  isJoinModalOpen: boolean;
  setJoinModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  users: SessionUser[];
  connection: signalR.HubConnection | null;
  copied: boolean;
  setCopied: React.Dispatch<React.SetStateAction<boolean>>;
  joinError: CollabErrorMap;
  handleHostSessionModal: () => void;
  handleJoinSessionModal: () => void;
  handleJoinSessionSubmit: (sessionId: string) => void;
  handleCopySessionId: () => void;
}

const CollabPanel: React.FC<CollabPanelProps> = ({
  isJoinModalOpen,
  setJoinModalOpen,
  users,
  copied,
  handleHostSessionModal,
  handleJoinSessionModal,
  handleJoinSessionSubmit,
  handleCopySessionId,
}) => {
  const { t } = useTranslation();
  const collab = useCollab();
  const { isAuthenticated } = useAuth();
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleSwitchToRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const handleSwitchToLogin = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };
  
  // When in session, show session details
  if (collab.sessionId) {
    return (
      <div className="collab-panel">
        <div className="collab-panel-inner" style={{ position: 'relative' }}>
          <div className="collab-session-title">
            <span>Session Started</span>
          </div>
          <div className="session-id-box" style={{ marginTop: 8 }}>
            <span className="session-id-text" title={collab.sessionId}>{collab.sessionId}</span>
            <button className="session-id-copy-icon" onClick={handleCopySessionId} title="Copy session ID">
              {copied ? 'Copied!' : <FaRegCopy size={16} />}
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 8 }}>
            <div className="collab-panel-desc" style={{ margin: 0 }}>Users in session:</div>
            <button
              title="Leave session"
              onClick={async () => { await collab.leaveSession(); }}
              className="collab-logout-btn"
              style={{ color: '#bdbdbd' }}
            >
              <MdLogout />
            </button>
          </div>
          <div className="user-card-list">
            {users.map(u => {
              const userDoc = collab.activeUserDocuments.find(doc => doc.name === u.name);
              return (
                <div className="user-card" key={u.name}>
                  <span className="user-dot" style={{ background: u.color }} />
                  <span className="user-name">
                    {u.name}
                    {u.isHost && (
                      <FaCrown
                        title="Host"
                        style={{ marginLeft: 6, color: "#bdbdbd", fontSize: "1em", paddingTop: 3 }}
                      />
                    )}
                  </span>
                  {userDoc?.activeFilePath && (
                    <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginTop: 2 }}>
                      {userDoc.activeFilePath.split(/[\\/]/).pop()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className="collab-panel">
        <div className="collab-panel-inner">
          <div className="collab-panel-title">
            {t('auth.loginToCollaborate')}
          </div>
          <div className="collab-panel-desc">
            {t('auth.loginDescription')}
          </div>
          <button 
            className="collab-login-btn"
            onClick={handleLoginClick}
          >
            {t('auth.loginButton')}
          </button>
        </div>
        
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSwitchToRegister={handleSwitchToRegister}
        />
        
        <RegisterModal
          isOpen={showRegisterModal}
          onClose={() => setShowRegisterModal(false)}
          onSwitchToLogin={handleSwitchToLogin}
        />
      </div>
    );
  }

  // Authenticated but not in session - show host/join buttons
  return (
    <div className="collab-panel">
      <div className="collab-panel-inner">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <UserAvatarSimple size={28} />
        </div>
        <div className="collab-panel-title">
          Let's code together
        </div>
        <div className="collab-panel-desc">
          Start collaboratively editing and<br />debugging with others in real-time.
        </div>
        <HostSessionButton onClick={handleHostSessionModal} />
        <JoinSessionButton onClick={handleJoinSessionModal} />
        {collab.collabLoading && <div style={{ color: '#bdbdbd', fontSize: 13, marginTop: 10 }}>Starting session...</div>}
      </div>
      {isJoinModalOpen && (
        <SessionIdModal onSubmit={handleJoinSessionSubmit} onClose={() => setJoinModalOpen(false)} />
      )}
    </div>
  );
};

export default CollabPanel; 